/**
 * Pure rules for the event-driven Perks refresh (COM-798 / COM-732): when a
 * shopper's saved Perks copy is due a re-read from Annex, and which form
 * submits change Perks enough to re-run the header and cart loaders.
 *
 * No page view runs a refresh. The server orchestrator is
 * `refreshPerks` in `./perks-refresh.server`.
 */
import type { ShouldRevalidateFunctionArgs } from "react-router";

import { zJson } from "~/lib/zod-helpers";

import { isOptedIn, parseLoyaltyData } from "./loyalty-data.logic";
import { UNKNOWN_POINTS, siteLoyaltySchema } from "./loyalty-data.schemas";
import {
  PERKS_ATTEMPTS_KEY,
  PERKS_PENDING_ENROLL_KEY,
  PERKS_REFRESHED_AT_KEY,
  PERKS_UNCONFIRMED_AT_KEY,
  loyaltyLastUpdatedSchema,
  pendingEnrollEntrySchema,
} from "./perks-refresh.schemas";
import type { PerksRefreshDueReason, PerksRefreshTrigger } from "./types";

const HOUR_MS = 3_600_000;

/**
 * How old a stamp may be before the trigger re-reads Annex. My Account is a
 * day, since orders and `/loyalty-perks` refresh through SFRA anyway; login is
 * an hour, SFRA `onLogin` parity for a shopper who signs in more than once.
 */
export const PERKS_REFRESH_MAX_AGE_MS = {
  "my-account": 24 * HOUR_MS,
  login: HOUR_MS,
} as const satisfies Record<PerksRefreshTrigger, number>;

/**
 * How long an unknown balance waits after a Workers read that stamped it
 * without points: Annex answered the balance read with a definitive 4xx (e.g.
 * AC1005 "user not found"), which asking again answers the same way. Without
 * it that shopper re-reads Annex on every My Account load and revalidation.
 * Only the Workers stamp counts here: SFRA's `loyaltyLastUpdated` is set on a
 * failed read too, and SFRA's rewrite drops `hecRefreshedAt`.
 */
export const PERKS_UNKNOWN_POINTS_RETRY_MS = HOUR_MS;

/**
 * How long a copy Workers saved unconfirmed (`hecUnconfirmedAt`: the tier read
 * did not answer) waits before it is due again. It overrides both stamps, so a
 * fresh SFRA `loyaltyLastUpdated` cannot hide the gap for a day, and it bounds
 * the retry, so a tier endpoint that keeps failing costs each shopper one
 * re-read per hour, not one per view.
 */
export const PERKS_UNCONFIRMED_RETRY_MS = HOUR_MS;

/**
 * The attempt floor: a refresh trigger never asks Annex for a shopper's Perks
 * twice within this, whatever Annex answered, whatever SFRA wrote and whatever
 * else the saved blob says. Checked before every other rule, from two sources:
 * this site's entry in the top-level `hecPerksAttemptedAt` map (persisted on
 * every attempt, and kept by SFRA's per-site rewrites) and the per-isolate
 * attempt map in `./perks-refresh.server` (which covers a write that failed or
 * raced, in that isolate). It is the structural bound; the per-reason windows
 * below only decide what is due once it has passed.
 */
export const PERKS_ATTEMPT_FLOOR_MS = HOUR_MS;

/**
 * A stamp further in the future than this is treated as garbage (due), so a
 * bad clock can never freeze a profile. Smaller skews read as fresh.
 */
const MAX_FUTURE_SKEW_MS = 5 * 60_000;

/**
 * This site's attempt stamp (its entry in the top-level `hecPerksAttemptedAt`
 * map), in epoch ms. `undefined` when there is no scope for this site, no map,
 * or the entry does not parse.
 */
export function perksAttemptOf(
  loyaltyData: string | undefined,
  siteId: string,
): number | undefined {
  const site = siteLoyaltySchema(siteId).safeParse(loyaltyData);
  if (!site.success) return undefined;
  const attempts = site.data.blob[PERKS_ATTEMPTS_KEY];
  return attempts && typeof attempts === "object" && !Array.isArray(attempts)
    ? loyaltyLastUpdatedSchema.parse(
        (attempts as Record<string, unknown>)[siteId],
      )
    : undefined;
}

/**
 * This site's Workers stamp (`hecRefreshedAt` inside its scope), in epoch ms.
 * `undefined` when there is no scope or the stamp does not parse.
 */
export function perksStampOf(
  loyaltyData: string | undefined,
  siteId: string,
): number | undefined {
  const site = siteLoyaltySchema(siteId).safeParse(loyaltyData);
  return site.success
    ? loyaltyLastUpdatedSchema.parse(site.data.scope[PERKS_REFRESHED_AT_KEY])
    : undefined;
}

/**
 * This site's unconfirmed marker (`hecUnconfirmedAt`). `undefined` when the
 * scope has none; `{ ms: undefined }` when it is set but does not parse, which
 * the staleness gate treats as due.
 */
export function perksUnconfirmedOf(
  loyaltyData: string | undefined,
  siteId: string,
): { ms: number | undefined } | undefined {
  const site = siteLoyaltySchema(siteId).safeParse(loyaltyData);
  if (!site.success || !(PERKS_UNCONFIRMED_AT_KEY in site.data.scope)) {
    return undefined;
  }
  return {
    ms: loyaltyLastUpdatedSchema.parse(
      site.data.scope[PERKS_UNCONFIRMED_AT_KEY],
    ),
  };
}

/**
 * When this site's Perks copy was last confirmed against Annex, in epoch ms:
 * the later of Workers' `hecRefreshedAt` (inside this site's scope) and SFRA's
 * `loyaltyLastUpdated` profile attribute. `undefined` when neither parses.
 */
export function perksLastRefreshedMs(
  loyaltyData: string | undefined,
  siteId: string,
  loyaltyLastUpdated: string | undefined,
): number | undefined {
  const stamp = perksStampOf(loyaltyData, siteId);
  const sfra = loyaltyLastUpdatedSchema.parse(loyaltyLastUpdated);
  if (stamp === undefined) return sfra;
  if (sfra === undefined) return stamp;
  return Math.max(stamp, sfra);
}

/** Whether `stampMs` is a usable stamp no older than `maxAgeMs`. */
const withinWindow = (
  stampMs: number | undefined,
  now: number,
  maxAgeMs: number,
): boolean =>
  stampMs !== undefined &&
  stampMs <= now + MAX_FUTURE_SKEW_MS &&
  now - stampMs <= maxAgeMs;

/**
 * Whether `trigger` should re-read Annex for this shopper. Only an opted-in
 * scope for THIS site is ever due. Then the attempt floor, before anything
 * else: an attempt (this site's `hecPerksAttemptedAt` entry, or `isolateAttemptedMs`)
 * under {@link PERKS_ATTEMPT_FLOOR_MS} old is never due. An attempt stamp that
 * does not parse, or lies over five minutes in the future, is ignored, so a
 * bad value can neither freeze refreshes nor, past the isolate map, loop them.
 * Past the floor: a copy saved unconfirmed is due once both
 * its marker and its Workers stamp are {@link PERKS_UNCONFIRMED_RETRY_MS} old,
 * whatever SFRA's stamp says (an unparseable marker defers to the Workers stamp);
 * an unknown balance is due unless a Workers read stamped it within
 * {@link PERKS_UNKNOWN_POINTS_RETRY_MS} (a definitive balance 4xx); otherwise
 * the stamp decides.
 */
export function perksRefreshDue({
  trigger,
  siteId,
  loyaltyData,
  lastUpdatedMs,
  isolateAttemptedMs,
  now,
}: {
  trigger: PerksRefreshTrigger;
  siteId: string;
  loyaltyData: string | undefined;
  lastUpdatedMs: number | undefined;
  /** When this isolate last asked Annex for this shopper and site. */
  isolateAttemptedMs?: number;
  now: number;
}): { due: boolean; reason: PerksRefreshDueReason } {
  const loyalty = parseLoyaltyData({ c_acLoyaltyData: loyaltyData }, siteId);
  if (!loyalty || !isOptedIn(loyalty)) {
    return { due: false, reason: "not-member" };
  }
  if (
    withinWindow(
      perksAttemptOf(loyaltyData, siteId),
      now,
      PERKS_ATTEMPT_FLOOR_MS,
    ) ||
    withinWindow(isolateAttemptedMs, now, PERKS_ATTEMPT_FLOOR_MS)
  ) {
    return { due: false, reason: "attempt-floor" };
  }
  const unconfirmed = perksUnconfirmedOf(loyaltyData, siteId);
  if (unconfirmed) {
    const retryMs = Math.min(
      PERKS_UNCONFIRMED_RETRY_MS,
      PERKS_REFRESH_MAX_AGE_MS[trigger],
    );
    // Measured from the later of the marker and the Workers stamp: every
    // Workers read restarts the window, so a read that stamps without
    // touching the marker (a definitive balance 4xx) cannot leave an old or
    // unparseable marker due on every view.
    const since = Math.max(
      unconfirmed.ms ?? -Infinity,
      perksStampOf(loyaltyData, siteId) ?? -Infinity,
    );
    return withinWindow(
      Number.isFinite(since) ? since : undefined,
      now,
      retryMs,
    )
      ? { due: false, reason: "fresh" }
      : { due: true, reason: "unconfirmed" };
  }
  if (!loyalty.pointsKnown) {
    const retryMs = Math.min(
      PERKS_UNKNOWN_POINTS_RETRY_MS,
      PERKS_REFRESH_MAX_AGE_MS[trigger],
    );
    return withinWindow(perksStampOf(loyaltyData, siteId), now, retryMs)
      ? { due: false, reason: "fresh" }
      : { due: true, reason: "unknown-points" };
  }
  return withinWindow(lastUpdatedMs, now, PERKS_REFRESH_MAX_AGE_MS[trigger])
    ? { due: false, reason: "fresh" }
    : { due: true, reason: "stale" };
}

/**
 * How long a pending sign-up enrollment (COM-798) waits between attempts. Short,
 * unlike the hour-long attempt floor: the shopper asked to enroll and sees
 * "Enroll Now" until it lands, and a pending marker only exists for a signup
 * whose enrollment failed, so the cost is bounded to those few shoppers. The
 * 429 backoff and the breaker still gate every attempt.
 */
export const PERKS_PENDING_ENROLL_RETRY_MS = 60_000;

/**
 * Past this a pending sign-up enrollment is abandoned: the shopper still has
 * "Enroll Now", and an enrollment Annex kept refusing for a month is not a
 * transient fault worth retrying on every view.
 */
export const PERKS_PENDING_ENROLL_MAX_AGE_MS = 30 * 24 * HOUR_MS;

/** The parsed blob as a plain object, or `undefined` when it is not one. */
function parseBlob(
  loyaltyData: string | undefined,
): Record<string, unknown> | undefined {
  const blob = zJson(loyaltyData);
  return blob && typeof blob === "object" && !Array.isArray(blob)
    ? (blob as Record<string, unknown>)
    : undefined;
}

/** The `hecPerksPendingEnroll` map, or `undefined` when there is none. */
function pendingMapOf(
  blob: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  const map = blob?.[PERKS_PENDING_ENROLL_KEY];
  return map && typeof map === "object" && !Array.isArray(map)
    ? (map as Record<string, unknown>)
    : undefined;
}

/**
 * Whether the Perks refresh should finish this site's pending sign-up enrollment
 * now (COM-798). Only when the site has NO scope at all: any scope, an opted-out
 * one included, means Annex or SFRA has spoken since signup, and re-enrolling
 * over it could undo a deliberate opt-out. Then the entry must parse, be under
 * {@link PERKS_PENDING_ENROLL_MAX_AGE_MS} old, and neither its persisted
 * `attemptedAt` nor this isolate's attempt may be under
 * {@link PERKS_PENDING_ENROLL_RETRY_MS} old.
 */
export function pendingEnrollmentDue({
  siteId,
  loyaltyData,
  isolateAttemptedMs,
  now,
}: {
  siteId: string;
  loyaltyData: string | undefined;
  isolateAttemptedMs?: number;
  now: number;
}): boolean {
  // The key's presence, not the scope parsing: a broken value under it (a
  // string, null, an array) is still Annex or SFRA having spoken, and the
  // enrolled write would overwrite it. `optInStatus` at the top is the flat
  // legacy scope.
  const blob = parseBlob(loyaltyData);
  if (!blob || siteId in blob || "optInStatus" in blob) return false;
  const entry = pendingEnrollEntrySchema.safeParse(
    pendingMapOf(blob)?.[siteId],
  );
  if (!entry.success) return false;
  const since = Date.parse(entry.data.since);
  if (!withinWindow(since, now, PERKS_PENDING_ENROLL_MAX_AGE_MS)) return false;
  const attempted = entry.data.attemptedAt
    ? Date.parse(entry.data.attemptedAt)
    : undefined;
  return (
    !withinWindow(attempted, now, PERKS_PENDING_ENROLL_RETRY_MS) &&
    !withinWindow(isolateAttemptedMs, now, PERKS_PENDING_ENROLL_RETRY_MS)
  );
}

/**
 * The blob after a pending enrollment attempt that did not land: this site's
 * entry keeps its `since` and records `attemptedAt`, so other isolates wait
 * {@link PERKS_PENDING_ENROLL_RETRY_MS} too. `null` when there is no entry.
 */
export function withPendingEnrollAttempt(
  loyaltyData: string | undefined,
  siteId: string,
  attemptedAt: string,
): string | null {
  const blob = parseBlob(loyaltyData);
  const map = pendingMapOf(blob);
  const entry = pendingEnrollEntrySchema.safeParse(map?.[siteId]);
  if (!blob || !map || !entry.success) return null;
  return JSON.stringify({
    ...blob,
    [PERKS_PENDING_ENROLL_KEY]: {
      ...map,
      [siteId]: { since: entry.data.since, attemptedAt },
    },
  });
}

/**
 * The blob once a pending enrollment landed: this site's scope as signup would
 * have saved it with both reads unanswered (opted in, balance unknown, no tier
 * and no stamp, so the next refresh reads them), and this site's pending entry
 * removed (the whole key when it was the last one). Every other key is kept.
 */
export function withEnrolledScope(
  loyaltyData: string | undefined,
  siteId: string,
  optInStatus: string,
): string {
  const blob = parseBlob(loyaltyData) ?? {};
  const { [siteId]: _done, ...otherPending } = pendingMapOf(blob) ?? {};
  const { [PERKS_PENDING_ENROLL_KEY]: _map, ...rest } = blob;
  return JSON.stringify({
    ...rest,
    ...(Object.keys(otherPending).length > 0 && {
      [PERKS_PENDING_ENROLL_KEY]: otherPending,
    }),
    [siteId]: {
      optInStatus,
      availablePoints: UNKNOWN_POINTS,
      lifetimePoints: UNKNOWN_POINTS,
      rewards: [],
    },
  });
}

/** The two resource routes that credit Perks points from a Workers page. */
const PERKS_MUTATION_ACTIONS = [
  "/resources/deadline-tracker",
  "/resources/learning-center-award",
] as const;

/**
 * True for a deadline-tracker save or Learning Center award that GRANTED
 * points. Root and layout `shouldRevalidate` both call it, so the header
 * dropdown and the cart banner pick up the new balance and tier without a
 * reload, and the two cannot drift (same reason as `auth-revalidate.ts`).
 *
 * Gated on the action's own grant flag (`rewardGranted` / `awarded`), not
 * only on the status: both routes answer 200 when nothing was credited, and
 * those must not re-run the chrome loaders.
 */
export function isPerksMutationSubmit({
  formMethod,
  formAction,
  actionStatus,
  actionResult,
}: Pick<
  ShouldRevalidateFunctionArgs,
  "formMethod" | "formAction" | "actionStatus" | "actionResult"
>): boolean {
  if (formMethod !== "POST" || !formAction) return false;
  if (!PERKS_MUTATION_ACTIONS.some(path => formAction.includes(path))) {
    return false;
  }
  if (actionStatus !== undefined && actionStatus >= 400) return false;
  if (typeof actionResult !== "object" || actionResult === null) return false;
  const result = actionResult as { rewardGranted?: unknown; awarded?: unknown };
  return result.rewardGranted === true || result.awarded === true;
}
